import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Search, Filter, Play, Clock, Dumbbell, Zap, 
  Wind, Target, Sparkles, ChevronRight, X,
  CircleDot, Square, Triangle, ExternalLink, Youtube
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Skill category definitions
const skillCategories = [
  { id: 'speed', name: 'Speed', icon: Wind, color: 'bg-blue-500', description: 'Sprint mechanics, acceleration, top-end speed' },
  { id: 'agility', name: 'Agility', icon: Zap, color: 'bg-yellow-500', description: 'Change of direction, reaction, footwork' },
  { id: 'strength', name: 'Strength', icon: Dumbbell, color: 'bg-red-500', description: 'Bodyweight, resistance, power building' },
  { id: 'plyometrics', name: 'Plyometrics', icon: Target, color: 'bg-purple-500', description: 'Explosive movements, jumping, bounding' },
  { id: 'flexibility', name: 'Flexibility', icon: Sparkles, color: 'bg-green-500', description: 'Mobility, stretching, injury prevention' },
];

// Sport filters
const sports = [
  { id: 'all', name: 'All Sports', icon: '🏆' },
  { id: 'football', name: 'Football', icon: '🏈' },
  { id: 'basketball', name: 'Basketball', icon: '🏀' },
  { id: 'baseball', name: 'Baseball', icon: '⚾' },
  { id: 'soccer', name: 'Soccer', icon: '⚽' },
  { id: 'general', name: 'General', icon: '💪' },
];

// Difficulty levels
const difficulties = [
  { id: 'beginner', name: 'Beginner', color: 'text-green-400' },
  { id: 'intermediate', name: 'Intermediate', color: 'text-yellow-400' },
  { id: 'advanced', name: 'Advanced', color: 'text-red-400' },
];

// Equipment options
const equipmentOptions = [
  { id: 'none', name: 'No Equipment' },
  { id: 'basic', name: 'Basic (cones, bands)' },
  { id: 'full', name: 'Full Gym' },
];

// Exercise database
const exerciseDatabase = [
  // SPEED EXERCISES
  {
    id: 'sprint_10yd',
    name: '10-Yard Sprint',
    category: 'speed',
    sports: ['football', 'basketball', 'baseball', 'soccer', 'general'],
    difficulty: 'beginner',
    equipment: 'none',
    duration: '5-10 min',
    sets: '6-8',
    reps: '1 sprint',
    rest: '30-45 sec',
    description: 'Explosive short burst acceleration. Focus on driving knees and pumping arms.',
    instructions: [
      'Start in athletic stance or 3-point stance',
      'Explode forward driving knees high',
      'Pump arms aggressively',
      'Stay low for first 3 steps',
      'Gradually rise to upright sprint position'
    ],
    coachTips: 'First 3 steps are everything. Stay patient and don\'t pop up too early.',
    videoUrl: 'https://www.youtube.com/watch?v=6c4rwLDVYnk'
  },
  {
    id: 'sprint_40yd',
    name: '40-Yard Dash',
    category: 'speed',
    sports: ['football', 'general'],
    difficulty: 'intermediate',
    equipment: 'none',
    duration: '10-15 min',
    sets: '4-6',
    reps: '1 sprint',
    rest: '2-3 min',
    description: 'The gold standard football speed test. Full acceleration to top-end speed.',
    instructions: [
      'Start in 3-point or 4-point stance',
      'Drive out low for first 10 yards',
      'Transition to upright sprinting',
      'Maintain form through finish line',
      'Run through the line, don\'t slow down early'
    ],
    coachTips: 'The 40 is won in the first 10 yards. Master your start.',
    videoUrl: 'https://www.youtube.com/watch?v=PRZmcYCyY-E'
  },
  {
    id: 'hill_sprints',
    name: 'Hill Sprints',
    category: 'speed',
    sports: ['football', 'basketball', 'baseball', 'soccer', 'general'],
    difficulty: 'intermediate',
    equipment: 'none',
    duration: '15-20 min',
    sets: '8-10',
    reps: '1 sprint (20-40 yards)',
    rest: 'Walk back down',
    description: 'Build explosive power and acceleration on an incline.',
    instructions: [
      'Find a moderate hill (10-15% grade)',
      'Sprint up with high knees and arm drive',
      'Walk back down for recovery',
      'Focus on powerful push-off each step',
      'Maintain forward lean into the hill'
    ],
    coachTips: 'Hills build champions. The resistance creates explosive power.',
    videoUrl: 'https://www.youtube.com/watch?v=6o0iGhiP5dY'
  },
  {
    id: 'flying_30s',
    name: 'Flying 30s',
    category: 'speed',
    sports: ['football', 'basketball', 'soccer', 'general'],
    difficulty: 'advanced',
    equipment: 'none',
    duration: '15 min',
    sets: '4-6',
    reps: '1 sprint',
    rest: '3-4 min',
    description: 'Top-end speed work. Build up to max velocity before the timing zone.',
    instructions: [
      'Start with 20-yard acceleration zone',
      'Build to near-max speed',
      'Sprint through 30-yard timing zone at max effort',
      'Focus on relaxed, efficient mechanics',
      'Long, powerful strides'
    ],
    coachTips: 'Relaxation is key at top speed. Tight muscles = slow muscles.',
    videoUrl: 'https://www.youtube.com/watch?v=2jfW5CKGh0c'
  },

  // AGILITY EXERCISES
  {
    id: 'ladder_basic',
    name: 'Agility Ladder - Quick Feet',
    category: 'agility',
    sports: ['football', 'basketball', 'baseball', 'soccer', 'general'],
    difficulty: 'beginner',
    equipment: 'basic',
    duration: '10 min',
    sets: '3-4',
    reps: '2 lengths',
    rest: '30 sec',
    description: 'Foundation footwork drill. Two feet in each box as fast as possible.',
    instructions: [
      'Start at one end of ladder',
      'Step both feet into each box',
      'Stay on balls of feet',
      'Keep hips low and arms pumping',
      'Look ahead, not at feet'
    ],
    coachTips: 'Speed comes from consistency. Master the pattern first, then add speed.',
    videoUrl: 'https://www.youtube.com/watch?v=2uqYrR5yCMM'
  },
  {
    id: 'ladder_ickey',
    name: 'Ickey Shuffle',
    category: 'agility',
    sports: ['football', 'basketball', 'soccer', 'general'],
    difficulty: 'intermediate',
    equipment: 'basic',
    duration: '10 min',
    sets: '3-4',
    reps: '2 lengths',
    rest: '30 sec',
    description: 'Lateral movement with rhythm. In-in-out pattern.',
    instructions: [
      'Start beside the ladder',
      'Step in with lead foot, then trail foot',
      'Step out with lead foot',
      'Repeat on opposite side',
      'Create a rhythmic shuffle pattern'
    ],
    coachTips: 'Named after NFL player Ickey Woods. Find your rhythm!',
    videoUrl: 'https://www.youtube.com/watch?v=0D-qlOZsL_U'
  },
  {
    id: 'cone_5_10_5',
    name: '5-10-5 Pro Agility',
    category: 'agility',
    sports: ['football', 'basketball', 'general'],
    difficulty: 'intermediate',
    equipment: 'basic',
    duration: '10-15 min',
    sets: '4-6',
    reps: '1',
    rest: '1-2 min',
    description: 'The NFL Combine classic. Tests lateral quickness and change of direction.',
    instructions: [
      'Straddle middle cone in 3-point stance',
      'Sprint 5 yards to first cone, touch line',
      'Sprint 10 yards to far cone, touch line',
      'Sprint 5 yards back through middle',
      'Stay low on cuts'
    ],
    coachTips: 'Win on the turns. Plant hard and explode out of cuts.',
    videoUrl: 'https://www.youtube.com/results?search_query=athletic+training+exercise'
  },
  {
    id: 'cone_t_drill',
    name: 'T-Drill',
    category: 'agility',
    sports: ['basketball', 'football', 'soccer', 'general'],
    difficulty: 'intermediate',
    equipment: 'basic',
    duration: '10-15 min',
    sets: '4-6',
    reps: '1',
    rest: '1-2 min',
    description: 'Multi-directional agility test. Forward, lateral, and backward movement.',
    instructions: [
      'Set 4 cones in T shape (10yd stem, 5yd each side)',
      'Sprint forward to center cone',
      'Shuffle left to cone, touch',
      'Shuffle right to far cone, touch',
      'Shuffle back to center, backpedal to start'
    ],
    coachTips: 'Great for basketball defenders and football DBs.',
    videoUrl: 'https://www.youtube.com/results?search_query=athletic+training+exercise'
  },
  {
    id: 'reaction_mirror',
    name: 'Mirror Drill',
    category: 'agility',
    sports: ['basketball', 'football', 'soccer', 'general'],
    difficulty: 'intermediate',
    equipment: 'none',
    duration: '10 min',
    sets: '4-6',
    reps: '20-30 sec',
    rest: '30 sec',
    description: 'Partner reaction drill. Mirror your partner\'s movements.',
    instructions: [
      'Face partner 3-5 yards apart',
      'One person leads with random movements',
      'Other person mirrors as quickly as possible',
      'Include shuffles, forward/back, jump stops',
      'Switch roles after each set'
    ],
    coachTips: 'Great for developing defensive instincts and reaction time.',
    videoUrl: 'https://www.youtube.com/results?search_query=athletic+training+exercise'
  },

  // STRENGTH EXERCISES
  {
    id: 'pushup_standard',
    name: 'Push-Ups',
    category: 'strength',
    sports: ['football', 'basketball', 'baseball', 'soccer', 'general'],
    difficulty: 'beginner',
    equipment: 'none',
    duration: '5-10 min',
    sets: '3',
    reps: '10-15',
    rest: '45-60 sec',
    description: 'Foundation upper body exercise. Chest, shoulders, triceps.',
    instructions: [
      'Start in plank position, hands shoulder-width',
      'Lower chest to ground with control',
      'Keep core tight, body in straight line',
      'Push back up to start position',
      'Don\'t let hips sag or pike up'
    ],
    coachTips: 'Quality over quantity. A perfect push-up beats 50 sloppy ones.',
    videoUrl: 'https://www.youtube.com/results?search_query=athletic+training+exercise'
  },
  {
    id: 'squat_bodyweight',
    name: 'Bodyweight Squats',
    category: 'strength',
    sports: ['football', 'basketball', 'baseball', 'soccer', 'general'],
    difficulty: 'beginner',
    equipment: 'none',
    duration: '5-10 min',
    sets: '3',
    reps: '15-20',
    rest: '45-60 sec',
    description: 'Foundation lower body movement. Builds leg strength and mobility.',
    instructions: [
      'Stand feet shoulder-width apart',
      'Sit back and down like sitting in a chair',
      'Keep chest up, knees tracking over toes',
      'Lower until thighs parallel to ground',
      'Drive through heels to stand'
    ],
    coachTips: 'If knees cave in, focus on pushing them out over pinky toes.',
    videoUrl: 'https://www.youtube.com/results?search_query=athletic+training+exercise'
  },
  {
    id: 'lunge_walking',
    name: 'Walking Lunges',
    category: 'strength',
    sports: ['football', 'basketball', 'baseball', 'soccer', 'general'],
    difficulty: 'beginner',
    equipment: 'none',
    duration: '5-10 min',
    sets: '3',
    reps: '10 each leg',
    rest: '45-60 sec',
    description: 'Single-leg strength and balance. Great for athletic power.',
    instructions: [
      'Step forward into lunge position',
      'Lower back knee toward ground',
      'Keep front knee behind toes',
      'Push through front heel to step forward',
      'Alternate legs with each step'
    ],
    coachTips: 'Single-leg strength prevents injuries and builds real athletic power.',
    videoUrl: 'https://www.youtube.com/results?search_query=athletic+training+exercise'
  },
  {
    id: 'plank_standard',
    name: 'Plank Hold',
    category: 'strength',
    sports: ['football', 'basketball', 'baseball', 'soccer', 'general'],
    difficulty: 'beginner',
    equipment: 'none',
    duration: '5 min',
    sets: '3',
    reps: '30-60 sec hold',
    rest: '30 sec',
    description: 'Core stability foundation. Builds strength for all athletic movements.',
    instructions: [
      'Start on forearms and toes',
      'Keep body in straight line from head to heels',
      'Engage core by pulling belly button to spine',
      'Don\'t let hips sag or pike up',
      'Breathe steadily throughout'
    ],
    coachTips: 'A strong core is the foundation of every athletic movement.',
    videoUrl: 'https://www.youtube.com/results?search_query=athletic+training+exercise'
  },
  {
    id: 'pullup',
    name: 'Pull-Ups',
    category: 'strength',
    sports: ['football', 'basketball', 'baseball', 'general'],
    difficulty: 'intermediate',
    equipment: 'basic',
    duration: '10 min',
    sets: '3-4',
    reps: 'Max reps',
    rest: '90 sec',
    description: 'Upper body pulling strength. Builds back, biceps, and grip.',
    instructions: [
      'Hang from bar with overhand grip',
      'Pull chest toward bar',
      'Keep core engaged throughout',
      'Lower with control to full hang',
      'Avoid swinging or kipping'
    ],
    coachTips: 'Can\'t do one yet? Start with negatives (jump up, lower slowly).',
    videoUrl: 'https://www.youtube.com/results?search_query=athletic+training+exercise'
  },
  {
    id: 'band_resisted_shuffle',
    name: 'Band Resisted Shuffles',
    category: 'strength',
    sports: ['basketball', 'football', 'soccer', 'general'],
    difficulty: 'intermediate',
    equipment: 'basic',
    duration: '10 min',
    sets: '3',
    reps: '10 each direction',
    rest: '45 sec',
    description: 'Hip and glute strengthening for lateral power.',
    instructions: [
      'Place band around ankles or above knees',
      'Get in athletic stance',
      'Take lateral steps against band resistance',
      'Keep hips low, chest up',
      'Control the band, don\'t let it snap you back'
    ],
    coachTips: 'Essential for defensive athletes. Strong hips = quick lateral movement.',
    videoUrl: 'https://www.youtube.com/results?search_query=athletic+training+exercise'
  },

  // PLYOMETRIC EXERCISES
  {
    id: 'box_jump',
    name: 'Box Jumps',
    category: 'plyometrics',
    sports: ['basketball', 'football', 'baseball', 'soccer', 'general'],
    difficulty: 'intermediate',
    equipment: 'basic',
    duration: '10-15 min',
    sets: '4',
    reps: '5-8',
    rest: '60-90 sec',
    description: 'Explosive lower body power. Builds vertical leap and fast-twitch fibers.',
    instructions: [
      'Stand facing box, feet shoulder-width',
      'Swing arms back, load hips',
      'Explode up, driving knees high',
      'Land softly on box in squat position',
      'Step down (don\'t jump down) to reset'
    ],
    coachTips: 'Land soft like a cat. Loud landings = wasted energy and injury risk.',
    videoUrl: 'https://www.youtube.com/results?search_query=athletic+training+exercise'
  },
  {
    id: 'broad_jump',
    name: 'Standing Broad Jump',
    category: 'plyometrics',
    sports: ['football', 'basketball', 'baseball', 'general'],
    difficulty: 'beginner',
    equipment: 'none',
    duration: '10 min',
    sets: '4',
    reps: '5',
    rest: '60 sec',
    description: 'Horizontal explosive power. NFL Combine test.',
    instructions: [
      'Stand with toes behind line',
      'Swing arms back, load hips',
      'Explode forward and up',
      'Drive arms forward for momentum',
      'Land softly and stick the landing'
    ],
    coachTips: 'Great measure of lower body power. Track your distance over time.',
    videoUrl: 'https://www.youtube.com/results?search_query=athletic+training+exercise'
  },
  {
    id: 'depth_jump',
    name: 'Depth Jumps',
    category: 'plyometrics',
    sports: ['basketball', 'football', 'general'],
    difficulty: 'advanced',
    equipment: 'basic',
    duration: '10 min',
    sets: '3',
    reps: '5',
    rest: '90-120 sec',
    description: 'Advanced reactive power. Step off box, immediately explode up.',
    instructions: [
      'Stand on low box (12-18 inches)',
      'Step off (don\'t jump off)',
      'Land on both feet',
      'Immediately explode into max vertical jump',
      'Minimize ground contact time'
    ],
    coachTips: 'Advanced exercise. Master box jumps first. Think: land and EXPLODE.',
    videoUrl: 'https://www.youtube.com/results?search_query=athletic+training+exercise'
  },
  {
    id: 'bounding',
    name: 'Bounding',
    category: 'plyometrics',
    sports: ['football', 'basketball', 'soccer', 'general'],
    difficulty: 'intermediate',
    equipment: 'none',
    duration: '10 min',
    sets: '4',
    reps: '20-30 yards',
    rest: '60-90 sec',
    description: 'Exaggerated running for power development. Long, explosive strides.',
    instructions: [
      'Start with slight jog',
      'Transition to exaggerated running strides',
      'Drive knee high, extend fully',
      'Maximum distance each bound',
      'Pump arms aggressively'
    ],
    coachTips: 'Think of yourself as leaping across a river. Maximum height and distance.',
    videoUrl: 'https://www.youtube.com/results?search_query=athletic+training+exercise'
  },
  {
    id: 'jump_squat',
    name: 'Jump Squats',
    category: 'plyometrics',
    sports: ['basketball', 'football', 'baseball', 'soccer', 'general'],
    difficulty: 'beginner',
    equipment: 'none',
    duration: '10 min',
    sets: '3',
    reps: '10',
    rest: '60 sec',
    description: 'Explosive squat variation. Builds vertical power.',
    instructions: [
      'Start in squat position',
      'Explode up into max jump',
      'Swing arms for momentum',
      'Land softly back in squat',
      'Immediately repeat'
    ],
    coachTips: 'Great for building game-ready explosiveness.',
    videoUrl: 'https://www.youtube.com/results?search_query=athletic+training+exercise'
  },

  // FLEXIBILITY EXERCISES
  {
    id: 'hip_flexor_stretch',
    name: 'Hip Flexor Stretch',
    category: 'flexibility',
    sports: ['football', 'basketball', 'baseball', 'soccer', 'general'],
    difficulty: 'beginner',
    equipment: 'none',
    duration: '5 min',
    sets: '2',
    reps: '30-45 sec each side',
    rest: 'None',
    description: 'Essential for athletes who sit in school all day. Opens hip flexors.',
    instructions: [
      'Kneel on one knee, other foot forward',
      'Keep torso upright',
      'Push hips forward gently',
      'Feel stretch in front of back hip',
      'Hold and breathe deeply'
    ],
    coachTips: 'Tight hip flexors slow you down. Stretch daily!',
    videoUrl: 'https://www.youtube.com/results?search_query=athletic+training+exercise'
  },
  {
    id: 'hamstring_stretch',
    name: 'Hamstring Stretch',
    category: 'flexibility',
    sports: ['football', 'basketball', 'baseball', 'soccer', 'general'],
    difficulty: 'beginner',
    equipment: 'none',
    duration: '5 min',
    sets: '2',
    reps: '30-45 sec each side',
    rest: 'None',
    description: 'Prevent hamstring strains. Critical for sprinting athletes.',
    instructions: [
      'Sit with one leg extended, other bent',
      'Reach toward extended foot',
      'Keep back straight, hinge at hips',
      'Feel stretch behind thigh',
      'Don\'t bounce, hold steady'
    ],
    coachTips: 'Hamstring injuries are preventable. Make this a daily habit.',
    videoUrl: 'https://www.youtube.com/results?search_query=athletic+training+exercise'
  },
  {
    id: 'world_greatest_stretch',
    name: 'World\'s Greatest Stretch',
    category: 'flexibility',
    sports: ['football', 'basketball', 'baseball', 'soccer', 'general'],
    difficulty: 'intermediate',
    equipment: 'none',
    duration: '5-10 min',
    sets: '2-3',
    reps: '5 each side',
    rest: 'Flow continuously',
    description: 'Dynamic full-body mobility. Opens hips, thoracic spine, and hamstrings.',
    instructions: [
      'Start in push-up position',
      'Step right foot outside right hand',
      'Drop left elbow toward ground',
      'Rotate right arm to ceiling',
      'Push hips back, straighten front leg',
      'Return to start, repeat other side'
    ],
    coachTips: 'Called "world\'s greatest" because it hits everything. Perfect warm-up.',
    videoUrl: 'https://www.youtube.com/results?search_query=athletic+training+exercise'
  },
  {
    id: 'foam_roll_quads',
    name: 'Foam Roll - Quads',
    category: 'flexibility',
    sports: ['football', 'basketball', 'baseball', 'soccer', 'general'],
    difficulty: 'beginner',
    equipment: 'basic',
    duration: '5 min',
    sets: '1',
    reps: '60-90 sec each leg',
    rest: 'None',
    description: 'Self-massage for quad recovery. Reduces soreness and improves mobility.',
    instructions: [
      'Lie face down with foam roller under thighs',
      'Support yourself on forearms',
      'Roll from hip to just above knee',
      'Pause on tender spots for 20-30 sec',
      'Control the pressure with your arms'
    ],
    coachTips: 'Foam rolling hurts so good. Do it after every workout.',
    videoUrl: 'https://www.youtube.com/results?search_query=athletic+training+exercise'
  },
  {
    id: 'shoulder_mobility',
    name: 'Shoulder Pass-Throughs',
    category: 'flexibility',
    sports: ['baseball', 'football', 'basketball', 'general'],
    difficulty: 'beginner',
    equipment: 'basic',
    duration: '5 min',
    sets: '2',
    reps: '10-15',
    rest: 'None',
    description: 'Shoulder mobility for throwing athletes. Opens chest and shoulders.',
    instructions: [
      'Hold PVC pipe or band with wide grip',
      'Keep arms straight throughout',
      'Lift arms overhead and behind body',
      'Return to front in controlled arc',
      'Narrow grip for more challenge'
    ],
    coachTips: 'Essential for baseball players and quarterbacks.',
    videoUrl: 'https://www.youtube.com/results?search_query=athletic+training+exercise'
  },
];

const ExerciseLibraryPage = () => {
  const { user } = useAuth();
  const [exercises, setExercises] = useState(exerciseDatabase);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSport, setSelectedSport] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [selectedEquipment, setSelectedEquipment] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Filter exercises
  const filteredExercises = exercises.filter(ex => {
    const matchesCategory = selectedCategory === 'all' || ex.category === selectedCategory;
    const matchesSport = selectedSport === 'all' || ex.sports.includes(selectedSport);
    const matchesDifficulty = selectedDifficulty === 'all' || ex.difficulty === selectedDifficulty;
    const matchesEquipment = selectedEquipment === 'all' || ex.equipment === selectedEquipment;
    const matchesSearch = ex.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         ex.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesCategory && matchesSport && matchesDifficulty && matchesEquipment && matchesSearch;
  });

  // Group exercises by category for display
  const groupedExercises = filteredExercises.reduce((acc, ex) => {
    if (!acc[ex.category]) acc[ex.category] = [];
    acc[ex.category].push(ex);
    return acc;
  }, {});

  const addToWorkout = async (exercise) => {
    try {
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/workouts`, {
        name: exercise.name,
        duration_minutes: parseInt(exercise.duration) || 15,
        intensity: exercise.difficulty === 'advanced' ? 'high' : exercise.difficulty === 'intermediate' ? 'moderate' : 'low',
        description: `${exercise.sets} x ${exercise.reps}\n\n${exercise.instructions.join('\n')}\n\nCoach Tip: ${exercise.coachTips}`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`"${exercise.name}" added to your workouts!`);
      setSelectedExercise(null);
    } catch (error) {
      toast.error('Failed to add exercise');
    }
  };

  const getCategoryInfo = (categoryId) => {
    return skillCategories.find(c => c.id === categoryId) || skillCategories[0];
  };

  const getDifficultyInfo = (difficultyId) => {
    return difficulties.find(d => d.id === difficultyId) || difficulties[0];
  };

  return (
    <DashboardLayout title="Exercise Library">
      <div className="px-4 py-6 max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
              Exercise Library
            </h1>
            <p className="text-aura-text-muted text-sm mt-1">
              {filteredExercises.length} exercises across 5 skill categories
            </p>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="space-y-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-aura-text-muted" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search exercises..."
                className="pl-10 bg-aura-surface border-white/10 text-white"
                data-testid="exercise-search"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`border-white/10 ${showFilters ? 'bg-white/10 text-white' : 'text-aura-text-muted'}`}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </div>

          {/* Filter Options */}
          {showFilters && (
            <Card className="bg-aura-surface border-white/10">
              <CardContent className="p-4 space-y-4">
                {/* Sport Filter */}
                <div>
                  <p className="text-xs text-aura-text-muted uppercase tracking-wider mb-2">Sport</p>
                  <div className="flex flex-wrap gap-2">
                    {sports.map(sport => (
                      <button
                        key={sport.id}
                        onClick={() => setSelectedSport(sport.id)}
                        className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                          selectedSport === sport.id
                            ? 'bg-aura-primary text-white'
                            : 'bg-white/5 text-aura-text-muted hover:bg-white/10'
                        }`}
                      >
                        {sport.icon} {sport.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Difficulty Filter */}
                <div>
                  <p className="text-xs text-aura-text-muted uppercase tracking-wider mb-2">Difficulty</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setSelectedDifficulty('all')}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        selectedDifficulty === 'all'
                          ? 'bg-aura-primary text-white'
                          : 'bg-white/5 text-aura-text-muted hover:bg-white/10'
                      }`}
                    >
                      All Levels
                    </button>
                    {difficulties.map(diff => (
                      <button
                        key={diff.id}
                        onClick={() => setSelectedDifficulty(diff.id)}
                        className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                          selectedDifficulty === diff.id
                            ? 'bg-aura-primary text-white'
                            : 'bg-white/5 text-aura-text-muted hover:bg-white/10'
                        }`}
                      >
                        {diff.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Equipment Filter */}
                <div>
                  <p className="text-xs text-aura-text-muted uppercase tracking-wider mb-2">Equipment</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setSelectedEquipment('all')}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        selectedEquipment === 'all'
                          ? 'bg-aura-primary text-white'
                          : 'bg-white/5 text-aura-text-muted hover:bg-white/10'
                      }`}
                    >
                      All
                    </button>
                    {equipmentOptions.map(eq => (
                      <button
                        key={eq.id}
                        onClick={() => setSelectedEquipment(eq.id)}
                        className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                          selectedEquipment === eq.id
                            ? 'bg-aura-primary text-white'
                            : 'bg-white/5 text-aura-text-muted hover:bg-white/10'
                        }`}
                      >
                        {eq.name}
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Skill Category Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              selectedCategory === 'all'
                ? 'bg-aura-primary text-white'
                : 'bg-white/5 text-aura-text-muted hover:bg-white/10'
            }`}
          >
            All Skills
          </button>
          {skillCategories.map(cat => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-2 ${
                  selectedCategory === cat.id
                    ? `${cat.color} text-white`
                    : 'bg-white/5 text-aura-text-muted hover:bg-white/10'
                }`}
                data-testid={`category-${cat.id}`}
              >
                <Icon className="w-4 h-4" />
                {cat.name}
              </button>
            );
          })}
        </div>

        {/* Exercise List */}
        {selectedCategory === 'all' ? (
          // Show grouped by category
          Object.entries(groupedExercises).map(([categoryId, categoryExercises]) => {
            const catInfo = getCategoryInfo(categoryId);
            const Icon = catInfo.icon;
            return (
              <div key={categoryId} className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-lg ${catInfo.color} flex items-center justify-center`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="font-heading font-bold text-lg text-white uppercase tracking-tight">
                    {catInfo.name}
                  </h2>
                  <span className="text-sm text-aura-text-muted">({categoryExercises.length})</span>
                </div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {categoryExercises.map(exercise => (
                    <ExerciseCard 
                      key={exercise.id} 
                      exercise={exercise} 
                      onSelect={() => setSelectedExercise(exercise)}
                      getCategoryInfo={getCategoryInfo}
                      getDifficultyInfo={getDifficultyInfo}
                    />
                  ))}
                </div>
              </div>
            );
          })
        ) : (
          // Show flat list for single category
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filteredExercises.map(exercise => (
              <ExerciseCard 
                key={exercise.id} 
                exercise={exercise} 
                onSelect={() => setSelectedExercise(exercise)}
                getCategoryInfo={getCategoryInfo}
                getDifficultyInfo={getDifficultyInfo}
              />
            ))}
          </div>
        )}

        {filteredExercises.length === 0 && (
          <div className="text-center py-12">
            <Dumbbell className="w-12 h-12 text-aura-text-muted mx-auto mb-4" />
            <p className="text-aura-text-muted">No exercises match your filters</p>
            <Button 
              variant="link" 
              onClick={() => {
                setSelectedCategory('all');
                setSelectedSport('all');
                setSelectedDifficulty('all');
                setSelectedEquipment('all');
                setSearchQuery('');
              }}
              className="text-aura-primary mt-2"
            >
              Clear all filters
            </Button>
          </div>
        )}

        {/* Exercise Detail Dialog */}
        <Dialog open={!!selectedExercise} onOpenChange={() => setSelectedExercise(null)}>
          <DialogContent className="bg-aura-surface border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
            {selectedExercise && (
              <>
                <DialogHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <DialogTitle className="font-heading text-xl uppercase tracking-tight">
                        {selectedExercise.name}
                      </DialogTitle>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <Badge className={`${getCategoryInfo(selectedExercise.category).color} text-white border-0`}>
                          {getCategoryInfo(selectedExercise.category).name}
                        </Badge>
                        <Badge variant="outline" className={`border-white/20 ${getDifficultyInfo(selectedExercise.difficulty).color}`}>
                          {getDifficultyInfo(selectedExercise.difficulty).name}
                        </Badge>
                        <Badge variant="outline" className="border-white/20 text-white">
                          {equipmentOptions.find(e => e.id === selectedExercise.equipment)?.name}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </DialogHeader>

                <div className="space-y-4 pt-4">
                  <p className="text-aura-text-muted">{selectedExercise.description}</p>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-4 gap-2">
                    <div className="bg-white/5 rounded-lg p-2 text-center">
                      <p className="text-xs text-aura-text-muted">Duration</p>
                      <p className="font-medium text-white">{selectedExercise.duration}</p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-2 text-center">
                      <p className="text-xs text-aura-text-muted">Sets</p>
                      <p className="font-medium text-white">{selectedExercise.sets}</p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-2 text-center">
                      <p className="text-xs text-aura-text-muted">Reps</p>
                      <p className="font-medium text-white">{selectedExercise.reps}</p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-2 text-center">
                      <p className="text-xs text-aura-text-muted">Rest</p>
                      <p className="font-medium text-white">{selectedExercise.rest}</p>
                    </div>
                  </div>

                  {/* Sports */}
                  <div>
                    <p className="text-xs text-aura-text-muted uppercase tracking-wider mb-2">Recommended For</p>
                    <div className="flex flex-wrap gap-1">
                      {selectedExercise.sports.map(sportId => {
                        const sport = sports.find(s => s.id === sportId);
                        return sport ? (
                          <span key={sportId} className="text-sm">
                            {sport.icon}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>

                  {/* Instructions */}
                  <div>
                    <p className="text-xs text-aura-text-muted uppercase tracking-wider mb-2">Instructions</p>
                    <ol className="space-y-2">
                      {selectedExercise.instructions.map((step, i) => (
                        <li key={i} className="flex gap-3 text-sm">
                          <span className="w-5 h-5 rounded-full bg-aura-primary/20 text-aura-primary flex items-center justify-center flex-shrink-0 text-xs">
                            {i + 1}
                          </span>
                          <span className="text-white">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>

                  {/* Coach Tip */}
                  <div className="bg-aura-warning/10 border border-aura-warning/30 rounded-lg p-3">
                    <p className="text-xs text-aura-warning uppercase tracking-wider mb-1">Coach Tip</p>
                    <p className="text-sm text-white">{selectedExercise.coachTips}</p>
                  </div>

                  {/* Watch Video Button */}
                  {selectedExercise.videoUrl && (
                    <a
                      href={selectedExercise.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                      data-testid="watch-video-btn"
                    >
                      <Youtube className="w-5 h-5" />
                      Watch Video Tutorial
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}

                  {/* Add to Workout Button */}
                  <Button
                    onClick={() => addToWorkout(selectedExercise)}
                    className="w-full bg-aura-primary hover:bg-aura-primary-hover text-white"
                    data-testid="add-to-workout-btn"
                  >
                    <Dumbbell className="w-4 h-4 mr-2" />
                    Add to My Workouts
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

// Exercise Card Component
const ExerciseCard = ({ exercise, onSelect, getCategoryInfo, getDifficultyInfo }) => {
  const catInfo = getCategoryInfo(exercise.category);
  const diffInfo = getDifficultyInfo(exercise.difficulty);
  const Icon = catInfo.icon;

  return (
    <Card 
      className="bg-aura-surface border-white/10 hover:border-white/20 transition-all cursor-pointer"
      onClick={onSelect}
      data-testid={`exercise-${exercise.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg ${catInfo.color} flex items-center justify-center flex-shrink-0`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-heading font-bold text-sm text-white uppercase tracking-tight truncate">
                {exercise.name}
              </h3>
              {exercise.videoUrl && (
                <Play className="w-3 h-3 text-red-500 flex-shrink-0" title="Video available" />
              )}
            </div>
            <p className="text-xs text-aura-text-muted mt-1 line-clamp-2">
              {exercise.description}
            </p>
            <div className="flex items-center gap-2 mt-2">
              <span className={`text-xs ${diffInfo.color}`}>{diffInfo.name}</span>
              <span className="text-xs text-aura-text-subtle">•</span>
              <span className="text-xs text-aura-text-subtle">{exercise.duration}</span>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-aura-text-muted flex-shrink-0" />
        </div>
      </CardContent>
    </Card>
  );
};

export default ExerciseLibraryPage;
